/**
 * RevenueService - Calculates revenue from various sources
 * 
 * FINANCE STRUCTURE DOCUMENTATION:
 * =================================
 * 
 * Student Finance Structure:
 * student.finance = [
 *   {
 *     term: "1st Term 2024 / 2025",    // Term identifier
 *     totalfees: 500,                   // Total fees for term (from class.classfees)
 *     othercharges: 50,                  // Additional charges
 *     balance: 200,                     // Remaining balance
 *     Arrears: 0,                        // Arrears from previous term
 *     breakdown: {},                    // Additional data (usually empty)
 *     payment: [                         // Array of payment transactions
 *       {
 *         date: "2024-01-15",           // Payment date (YYYY-MM-DD) - ALWAYS PRESENT
 *         time: "14:30",                // Payment time
 *         des: "School Fees",           // Description (empty = school fees, "Discount" = discount)
 *         amountpaid: 300,              // Amount paid
 *         balance: 200,                 // Balance after payment
 *         paidby: "John Doe",           // Payer name
 *         receiver: "Admin Name"         // Receiver name
 *       }
 *     ]
 *   }
 * ]
 * 
 * IMPORTANT NOTES:
 * - All payments in finance.payment[] are SCHOOL FEES (except payment.des === "Discount")
 * - payment.date is ALWAYS present (no fallback needed)
 * - Other charges (books, uniforms) are in student.charges[], NOT in finance.payment[]
 * - Multiple terms: Each student can have multiple finance records (one per term)
 * - Discounts: payment.des === "Discount" should be excluded from revenue
 */
class RevenueService {
  // Helper to format currency
  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toFixed(2);
  }

  // Calculate total amount paid from payment array (excluding discounts)
  totalAmountPaid(payments) {
    if (!payments || !Array.isArray(payments)) return 0;
    
    let total = 0;
    for (let i = 0; i < payments.length; i++) {
      if (payments[i].des !== "Discount") {
        total += parseFloat(payments[i].amountpaid) || 0;
      }
    }
    return total;
  }

  // School Fees Revenue
  // EXACT match to school fees page logic (tableschoolfees.js)
  // Key differences from previous version:
  // 1. Uses ONLY latest finance record: finance[finance.length-1] (not all terms)
  // 2. Only counts payments if term === mainterm (matching line 5801)
  // 3. Uses totalamountpaid function that includes ALL payments (even discounts for display)
  // Finance Structure Understanding:
  // - student.finance[] = array of finance records (one per term)
  // - financeRecord = { term, totalfees, othercharges, balance, Arrears, breakdown, payment[] }
  // - payment[] = array of payment transactions
  // - All payments in finance.payment[] are school fees (except payment.des === "Discount")
  // - payment.date is always present in format "YYYY-MM-DD"
  // - Other charges (books, uniforms) are in student.charges[], NOT in finance.payment[]
  calculateSchoolFeesRevenue(students, classData, startDate, endDate, mainterm = null) {
    // Helper function - EXACT match from tableschoolfees.js line 2984
    // This includes ALL payments (no discount exclusion for matching school fees page display)
    const totalamountpaid = (input) => {
      if (!input || !Array.isArray(input)) return 0;
      var result = 0;
      for (var i in input) {
        result += parseFloat(input[i].amountpaid) || 0;
      }
      return result;
    };

    let totalRevenue = 0;
    const revenueByClass = {};
    const revenueByTerm = {};
    const paymentMethods = { cash: 0, mobile_money: 0, bank: 0, other: 0 };
    let discountsGiven = 0;
    let studentsProcessed = 0;
    let studentsSkipped = 0;
    let studentsTermMismatch = 0;
    let paymentsIncluded = 0;
    let paymentsExcludedByDate = 0;
    let totalPaymentsAllTime = 0; // Track all payments (no date filter) - matches school fees page

    // Normalize dates to start/end of day for comparison
    // CRITICAL: Payment dates are created as: year + "-" + month + "-" + day
    // where: year=getFullYear() (local), month=getMonth()+1 (local), day=getUTCDate() (UTC)
    // We MUST match this exact format for string comparison to work
    const normalizedStartDate = new Date(startDate);
    // Extract components EXACTLY as payment dates are created
    const startYear = normalizedStartDate.getFullYear(); // Local year
    const startMonth = normalizedStartDate.getMonth(); // Local month (0-indexed)
    const startDay = normalizedStartDate.getUTCDate(); // UTC day
    // Create date using these exact components
    normalizedStartDate.setFullYear(startYear, startMonth, startDay);
    normalizedStartDate.setHours(0, 0, 0, 0);
    
    const normalizedEndDate = new Date(endDate);
    // Extract components EXACTLY as payment dates are created
    const endYear = normalizedEndDate.getFullYear(); // Local year
    const endMonth = normalizedEndDate.getMonth(); // Local month (0-indexed)
    const endDay = normalizedEndDate.getUTCDate(); // UTC day
    // Create date using these exact components
    normalizedEndDate.setFullYear(endYear, endMonth, endDay);
    normalizedEndDate.setHours(23, 59, 59, 999);

    // Helper to normalize term for comparison
    const normalizeTerm = (term) => {
      if (!term) return null;
      if (typeof term === 'string') return term.trim();
      if (typeof term === 'object' && term !== null) {
        return (term.name || term.term || term.value || String(term)).trim();
      }
      return String(term).trim();
    };

    // Normalize mainterm for comparison
    const normalizedMainterm = mainterm !== null && mainterm !== undefined && mainterm !== "none" 
      ? normalizeTerm(mainterm) 
      : null;

    // Helper to parse payment date - handles YYYY-MM-DD format
    // Payment dates are created using UTC (getUTCDate()), so we parse as UTC
    const parsePaymentDate = (dateString) => {
      if (!dateString) return null;
      
      // Handle YYYY-MM-DD format (most common)
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        const parts = dateString.split('T')[0].split('-'); // Handle ISO strings
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);
        // Create date using UTC to match payment date creation (getUTCDate())
        const date = new Date(Date.UTC(year, month, day));
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      
      // Fallback to standard Date parsing
      const date = new Date(dateString);
      return !isNaN(date.getTime()) ? date : null;
    };

    // Helper to format date as YYYY-MM-DD string
    // CRITICAL: Must match EXACT payment date format from tableschoolfees.js line 538-541:
    // const day = dt.getUTCDate(); (UTC day)
    // const month = String(dt.getMonth() + 1).padStart(2, '0'); (local month)
    // const year = dt.getFullYear(); (local year)
    // const date = year + "-" + month + "-" + day;
    const formatDateString = (date) => {
      if (!date || isNaN(date.getTime())) return null;
      // Match EXACT payment date format: year (local), month (local), day (UTC)
      const year = date.getFullYear(); // Local year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Local month
      const day = String(date.getUTCDate()).padStart(2, '0'); // UTC day
      return `${year}-${month}-${day}`;
    };

    // Helper to format date range boundary directly from input date
    // This ensures we use the same logic as payment date creation
    // Format boundaries ONCE from the original input dates, not from normalized dates
    // CRITICAL: Use the ORIGINAL startDate/endDate, not normalized dates
    const formatDateRangeBoundary = (date) => {
      if (!date || isNaN(date.getTime())) return null;
      // Extract components EXACTLY as payment dates are created
      const year = date.getFullYear(); // Local year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Local month
      const day = String(date.getUTCDate()).padStart(2, '0'); // UTC day
      return `${year}-${month}-${day}`;
    };

    // Format date range boundaries directly from input dates (not normalized dates)
    // This ensures we match payment date format exactly
    // These strings will be used for comparison with payment dates
    const startDateStr = formatDateRangeBoundary(startDate);
    const endDateStr = formatDateRangeBoundary(endDate);

    // Helper to check if payment is in date range
    // Uses date string comparison (YYYY-MM-DD) for exact matching
    // Payment dates are already strings in YYYY-MM-DD format, so we compare directly
    // startDateStr and endDateStr are passed in to ensure they're formatted correctly
    const isPaymentInRange = (payment, startDateStr, endDateStr) => {
      if (!payment || !payment.date) return false;
      if (!startDateStr || !endDateStr) return false;
      
      // Payment dates are stored as strings in YYYY-MM-DD format
      // Extract the date part if it's an ISO string with time
      let paymentDateStr = null;
      if (typeof payment.date === 'string') {
        // Extract YYYY-MM-DD part (handle ISO strings like "2026-02-06T00:00:00.000Z")
        paymentDateStr = payment.date.split('T')[0];
        // Validate format - must be exactly YYYY-MM-DD
        if (!/^\d{4}-\d{2}-\d{2}$/.test(paymentDateStr)) {
          return false;
        }
      } else if (payment.date instanceof Date) {
        // If it's a Date object, format it using the same logic as payment creation
        paymentDateStr = formatDateString(payment.date);
      } else {
        // Try to convert to string
        try {
          const dateObj = new Date(payment.date);
          if (!isNaN(dateObj.getTime())) {
            paymentDateStr = formatDateString(dateObj);
          } else {
            return false;
          }
        } catch (e) {
          return false;
        }
      }
      
      if (!paymentDateStr) return false;
      
      // Compare date strings directly (YYYY-MM-DD format)
      // This ensures exact date matching - both sides use same format
      const inRange = paymentDateStr >= startDateStr && paymentDateStr <= endDateStr;
      
      // Enhanced debug logging for troubleshooting (log more frequently)
      if (Math.random() < 0.05) { // Log 5% of comparisons for better debugging
        console.log('Date comparison:', {
          paymentDateStr,
          startDateStr,
          endDateStr,
          inRange,
          paymentDate: payment.date,
          comparison: `${paymentDateStr} >= ${startDateStr} && ${paymentDateStr} <= ${endDateStr}`
        });
      }
      
      return inRange;
    };

    students.forEach(student => {
      // Skip students with no finance data
      if (!student.finance || !Array.isArray(student.finance) || student.finance.length === 0) {
        studentsSkipped++;
        return;
      }

      // EXACT match to school fees page: Use ONLY latest finance record
      // Matching: row.finance[row.finance.length-1] (line 5801)
      const latestFinance = student.finance[student.finance.length - 1];
      
      if (!latestFinance || !latestFinance.payment || !Array.isArray(latestFinance.payment)) {
        studentsSkipped++;
        return;
      }

      // EXACT match to school fees page: Only process if term === mainterm
      // Matching: row.finance[row.finance.length-1].term === mainterm (line 5801)
      if (normalizedMainterm !== null) {
        const latestTermNormalized = normalizeTerm(latestFinance.term);
        
        // If term doesn't match, skip this student (like school fees page shows "No data")
        if (latestTermNormalized !== normalizedMainterm) {
          studentsTermMismatch++;
          return; // Skip - term doesn't match
        }
      }

      studentsProcessed++;

      // FIRST: Calculate ALL payments (matching school fees page - no date filter)
      // This is what the school fees page shows: totalamountpaid(row.finance[row.finance.length-1].payment)
      const allPayments = latestFinance.payment.filter(payment => {
        if (!payment || typeof payment !== 'object') return false;
        // Exclude discounts from revenue calculation
        return payment.des !== "Discount";
      });
      
      const allPaymentsTotal = totalamountpaid(allPayments);
      totalPaymentsAllTime += allPaymentsTotal;

      // SECOND: Filter payments by date range for period reports
      const paymentsInRange = latestFinance.payment.filter(payment => {
        if (!payment || typeof payment !== 'object') {
          return false;
        }

        // Track discounts separately (they reduce revenue, not add to it)
        if (payment.des === "Discount") {
          if (isPaymentInRange(payment, startDateStr, endDateStr)) {
            discountsGiven += parseFloat(payment.amountpaid) || 0;
          }
          return false; // Exclude discounts from revenue
        }

        // Check if payment is in date range using helper function
        // Pass the pre-formatted date strings to ensure exact matching
        const inRange = isPaymentInRange(payment, startDateStr, endDateStr);
        if (!inRange) {
          paymentsExcludedByDate++;
        } else {
          paymentsIncluded++;
        }
        
        return inRange;
      });

      // Calculate revenue using totalamountpaid (matching school fees page)
      // This includes ALL payments (discounts excluded above in filter)
      const termRevenue = totalamountpaid(paymentsInRange);
      
      if (termRevenue > 0) {
        totalRevenue += termRevenue;

        // Track by class
        const className = student.class_ || 'Unknown';
        if (!revenueByClass[className]) {
          revenueByClass[className] = 0;
        }
        revenueByClass[className] += termRevenue;

        // Track by term
        let termString = normalizeTerm(latestFinance.term) || 'Unknown';
        if (termString && termString !== 'Unknown') {
          if (!revenueByTerm[termString]) {
            revenueByTerm[termString] = 0;
          }
          revenueByTerm[termString] += termRevenue;
        }

        // Track payment methods
        paymentsInRange.forEach(payment => {
          const amount = parseFloat(payment.amountpaid) || 0;
          if (amount > 0) {
            const method = (payment.method || payment.paymentmethod || 'other').toLowerCase();
            if (method.includes('cash')) {
              paymentMethods.cash += amount;
            } else if (method.includes('mobile') || method.includes('momo')) {
              paymentMethods.mobile_money += amount;
            } else if (method.includes('bank') || method.includes('transfer')) {
              paymentMethods.bank += amount;
            } else {
              paymentMethods.other += amount;
            }
          }
        });
      }
    });

    // Enhanced debug logging
    // Note: startDateStr and endDateStr are already formatted above using formatDateRangeBoundary
    // from the original startDate/endDate (not normalized dates) to ensure exact matching
    console.log('School Fees Revenue Calculation (Period Synced):', {
      totalRevenue: totalRevenue.toFixed(2),
      totalPaymentsAllTime: totalPaymentsAllTime.toFixed(2), // All payments (no date filter) - should match school fees page
      difference: (totalPaymentsAllTime - totalRevenue).toFixed(2), // Difference due to date filtering
      mainterm: mainterm,
      normalizedMainterm: normalizedMainterm,
      studentsProcessed,
      studentsSkipped,
      studentsTermMismatch,
      paymentsIncluded,
      paymentsExcludedByDate,
      dateRange: {
        start: startDateStr, // Using formatDateString for exact payment date format
        end: endDateStr, // Using formatDateString for exact payment date format
        startTime: normalizedStartDate.getTime(),
        endTime: normalizedEndDate.getTime(),
        startDateStr: startDateStr,
        endDateStr: endDateStr,
        note: 'Date strings use exact payment date format: year (local) + month (local) + day (UTC)'
      },
      revenueByClass,
      revenueByTerm,
      paymentMethods,
      discountsGiven: discountsGiven.toFixed(2),
      calculationMethod: 'Uses date string comparison (YYYY-MM-DD) with exact payment date format matching',
      note: 'Date filtering uses date string comparison with EXACT payment date format (local year/month, UTC day). All payments in date range from latest finance record are included. totalPaymentsAllTime should match school fees page.'
    });

    return {
      totalRevenue,
      totalPaymentsAllTime, // Add this to compare with school fees page
      revenueByClass,
      revenueByTerm,
      paymentMethods,
      discountsGiven,
      studentCount: students.length
    };
  }

  // Sales Revenue
  calculateSalesRevenue(sales, startDate, endDate) {
    if (!sales || !Array.isArray(sales)) {
      return {
        totalRevenue: 0,
        revenueByCategory: {},
        paymentMethods: { cash: 0, mobile_money: 0, card: 0, credit: 0 },
        transactionCount: 0
      };
    }

    const filteredSales = sales.filter(sale => {
      const saleDate = new Date(sale.createdAt || sale.date || Date.now());
      return saleDate >= startDate && saleDate <= endDate;
    });

    let totalRevenue = 0;
    const revenueByCategory = {};
    const paymentMethods = { cash: 0, mobile_money: 0, card: 0, credit: 0 };

    filteredSales.forEach(sale => {
      const amount = parseFloat(sale.total) || 0;
      totalRevenue += amount;
      
      // Track payment methods
      const method = (sale.paymentMethod || 'cash').toLowerCase();
      if (method === 'cash') {
        paymentMethods.cash += amount;
      } else if (method === 'mobile_money' || method === 'mobile money') {
        paymentMethods.mobile_money += amount;
      } else if (method === 'card') {
        paymentMethods.card += amount;
      } else if (method === 'credit') {
        paymentMethods.credit += amount;
      }

      // Track by product category if available
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const category = item.category || item.productCategory || 'Uncategorized';
          if (!revenueByCategory[category]) {
            revenueByCategory[category] = 0;
          }
          revenueByCategory[category] += parseFloat(item.totalPrice || item.unitPrice * item.quantity) || 0;
        });
      }
    });

    return {
      totalRevenue,
      revenueByCategory,
      paymentMethods,
      transactionCount: filteredSales.length
    };
  }

  // Canteen Revenue
  calculateCanteenRevenue(students, startDate, endDate) {
    let totalRevenue = 0;
    const revenueByClass = {};
    const revenueByType = { daily: 0, weekly: 0, monthly: 0 };

    students.forEach(student => {
      if (!student.canteen || !Array.isArray(student.canteen)) return;

      student.canteen.forEach(canteenRecord => {
        const recordDate = new Date(canteenRecord.date || canteenRecord.createdAt || Date.now());
        if (recordDate >= startDate && recordDate <= endDate) {
          const amount = parseFloat(canteenRecord.amount || canteenRecord.amountpaid) || 0;
          totalRevenue += amount;

          if (!revenueByClass[student.class_]) {
            revenueByClass[student.class_] = 0;
          }
          revenueByClass[student.class_] += amount;

          // Track by type
          const type = (canteenRecord.type || 'daily').toLowerCase();
          if (revenueByType[type] !== undefined) {
            revenueByType[type] += amount;
          } else {
            revenueByType.daily += amount;
          }
        }
      });
    });

    return {
      totalRevenue,
      revenueByClass,
      revenueByType
    };
  }

  // Other Income (charges)
  calculateOtherIncome(students, startDate, endDate) {
    let totalIncome = 0;
    const incomeByType = {
      admission: 0,
      printing: 0,
      books: 0,
      uniform: 0,
      other: 0
    };

    students.forEach(student => {
      if (!student.charges || !Array.isArray(student.charges)) return;

      student.charges.forEach(charge => {
        const chargeDate = new Date(charge.date || charge.createdAt || Date.now());
        if (chargeDate >= startDate && chargeDate <= endDate) {
          const amount = parseFloat(charge.amount) || 0;
          totalIncome += amount;

          const type = (charge.type || charge.description || 'other').toLowerCase();
          if (type.includes('admission')) {
            incomeByType.admission += amount;
          } else if (type.includes('print')) {
            incomeByType.printing += amount;
          } else if (type.includes('book')) {
            incomeByType.books += amount;
          } else if (type.includes('uniform')) {
            incomeByType.uniform += amount;
          } else {
            incomeByType.other += amount;
          }
        }
      });
    });

    return {
      totalIncome,
      incomeByType
    };
  }

  // Total Revenue - Aggregates all revenue sources
  calculateTotalRevenue(students, sales, classData, startDate, endDate, mainterm = null) {
    const schoolFees = this.calculateSchoolFeesRevenue(students, classData, startDate, endDate, mainterm);
    const salesRevenue = this.calculateSalesRevenue(sales, startDate, endDate);
    const canteenRevenue = this.calculateCanteenRevenue(students, startDate, endDate);
    const otherIncome = this.calculateOtherIncome(students, startDate, endDate);

    return {
      schoolFees: schoolFees.totalRevenue,
      sales: salesRevenue.totalRevenue,
      canteen: canteenRevenue.totalRevenue,
      otherIncome: otherIncome.totalIncome,
      total: schoolFees.totalRevenue + salesRevenue.totalRevenue + 
              canteenRevenue.totalRevenue + otherIncome.totalIncome,
      breakdown: {
        schoolFees,
        sales: salesRevenue,
        canteen: canteenRevenue,
        otherIncome
      }
    };
  }
}

export default new RevenueService();
